// CUST-001 Guided Journey + Find My Pod
// Compiled by webpack to `assets/guided-journey.autojs.js`.
//
// State machine: hero -> selector -> panel(input -> loading -> result-match | result-none | error)
// Pure, DOM-free helpers (mapCategoryToOption, nextState, validateFile) are exported for unit tests.

import {
  createStepTracker,
  getSessionStorage,
  mapStateToStep,
  pushToDataLayer,
  recordJourneyStep,
} from '../objects/journey-analytics/index.js';

export const STORAGE_KEY = 'ub_guided_journey';

export const STATES = {
  HERO: 'hero',
  SELECTOR: 'selector',
  PANEL_INPUT: 'panel-input',
  PANEL_LOADING: 'panel-loading',
  RESULT_MATCH: 'result-match',
  RESULT_NONE: 'result-none',
  CONTACT: 'contact',
  CONTACT_SUCCESS: 'contact-success',
  ERROR: 'error',
};

export const EVENTS = {
  GET_STARTED: 'get-started',
  BACK: 'back',
  ASK_BREWSTER: 'ask-brewster',
  CLOSE_PANEL: 'close-panel',
  SUBMIT: 'submit',
  IDENTIFY_MATCH: 'identify-match',
  IDENTIFY_NONE: 'identify-none',
  IDENTIFY_ERROR: 'identify-error',
  OPEN_CONTACT: 'open-contact',
  CONTACT_SUBMITTED: 'contact-submitted',
  ASK_AGAIN: 'ask-again',
  // UBB-418 — purchase type, chosen on the hero before the selector opens.
  CHOOSE_SUBSCRIBE: 'choose-subscribe',
  CHOOSE_ONETIME: 'choose-onetime',
  TOGGLE_MODE: 'toggle-mode',
};

// Purchase types. SUBSCRIBE is the default so a restored session (or any path that
// reaches the selector without passing the hero) still has a usable URL.
export const MODES = { SUBSCRIBE: 'subscribe', ONETIME: 'onetime' };

// Persisted journey state goes stale after a day. The state lives in localStorage so it
// survives tab closes and restarts, and `redirect()` deliberately parks it on SELECTOR so
// returning from a bundle page resumes mid-journey — which also means a visitor returning
// days later would silently skip the hero, where the purchase type is now chosen.
export const STATE_TTL_MS = 24 * 60 * 60 * 1000;

// Journey persistence — OFF at the client's request, so every visit starts at the hero
// and the purchase type is always chosen explicitly. Flip to true to restore resuming
// mid-journey; the persist/restore machinery below is left intact for exactly that.
export const PERSIST_JOURNEY = false;

/** The other purchase type. */
export function otherMode(mode) {
  return mode === MODES.ONETIME ? MODES.SUBSCRIBE : MODES.ONETIME;
}

/** Does any option block have a bundle URL for this purchase type? */
export function hasCardsForMode(blocks, mode) {
  return (blocks || []).some((b) => bundleUrlForMode(b, mode));
}

/**
 * Is persisted state still usable? Stale or unstamped data is discarded so the journey
 * restarts at the hero. Unstamped covers state written before the TTL existed.
 */
export function isStateFresh(data, now = Date.now()) {
  if (!data || typeof data.savedAt !== 'number') return false;
  return now - data.savedAt < STATE_TTL_MS;
}

/**
 * The bundle URL a card should open for the active purchase mode.
 * Returns '' when the card has no URL for that mode — such cards are hidden.
 */
export function bundleUrlForMode(block, mode) {
  if (!block) return '';
  return mode === MODES.ONETIME
    ? block.bundleUrlOnetime || ''
    : block.bundleUrl || '';
}

// ---------------------------------------------------------------------------
// Pure helpers (no DOM, unit-tested in tests/unit/guided-journey.test.js)
// ---------------------------------------------------------------------------

/**
 * The CTAs a pod-match result should offer, as [{ url, label }].
 *
 * Standalone (the site-wide Find My Pod panel) has no hero, so the customer has not
 * picked a purchase type — it offers both, and drops the one-time button when that
 * match has no one-time URL. On the build-bundle page the type was already chosen on
 * the hero, so a single button honours that choice rather than asking twice.
 */
export function resultActions(option, { standalone, mode, labels = {} } = {}) {
  if (!option) return [];
  const subscribeLabel = labels.subscribe || 'Subscribe & Save';
  const onetimeLabel = labels.onetime || 'One-time purchase';

  if (!standalone) {
    const url = bundleUrlForMode(option, mode);
    if (!url) return [];
    const onetime = mode === MODES.ONETIME;
    return [
      {
        url,
        type: onetime ? MODES.ONETIME : MODES.SUBSCRIBE,
        label: onetime ? onetimeLabel : subscribeLabel,
      },
    ];
  }

  const actions = [];
  if (option.bundleUrl) {
    actions.push({
      url: option.bundleUrl,
      type: MODES.SUBSCRIBE,
      label: subscribeLabel,
    });
  }
  if (option.bundleUrlOnetime) {
    actions.push({
      url: option.bundleUrlOnetime,
      type: MODES.ONETIME,
      label: onetimeLabel,
    });
  }
  return actions;
}

/**
 * Map an identify-response category to the matching option block.
 * `none` or a falsy `match` always routes to the `beans` block.
 *
 * @param {string} category - 'nespresso' | 'aldi_caffitaly' | 'none'
 * @param {boolean} match
 * @param {Array<{categoryKey: string}>} blocks
 * @returns {object|null} the matching block, or null when none found
 */
export function mapCategoryToOption(category, match, blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return null;
  }
  const targetKey = !match || category === 'none' ? 'beans' : category;
  return (
    blocks.find((block) => block && block.categoryKey === targetKey) || null
  );
}

/**
 * Copy for the no-match result. The merchant's theme setting wins (UBB-422 — the
 * identify API always returns its own `message`, which used to override the setting
 * so editor changes never reached the storefront); the API copy is only a fallback
 * for a blank setting, then the built-in default.
 *
 * @param {string} settingMessage - section setting `nomatch_message`
 * @param {string} apiMessage - identify response `message`
 * @returns {string}
 */
export function noMatchMessage(settingMessage, apiMessage) {
  return (
    (settingMessage && settingMessage.trim()) ||
    apiMessage ||
    "Sadly we don't make pods for your machine yet. But the good news? Our beans are pretty great in any grinder."
  );
}

/**
 * Deterministic state transition for the guided journey.
 *
 * @param {string} state - current state (one of STATES)
 * @param {string} event - one of EVENTS
 * @returns {string} the next state (unchanged when the event is invalid)
 */
export function nextState(state, event) {
  switch (event) {
    case EVENTS.GET_STARTED:
      return state === STATES.HERO ? STATES.SELECTOR : state;
    case EVENTS.BACK:
      return STATES.HERO;
    case EVENTS.ASK_BREWSTER:
      return STATES.PANEL_INPUT;
    case EVENTS.CLOSE_PANEL:
      return STATES.SELECTOR;
    case EVENTS.SUBMIT:
      return state === STATES.PANEL_INPUT ? STATES.PANEL_LOADING : state;
    case EVENTS.IDENTIFY_MATCH:
      return STATES.RESULT_MATCH;
    case EVENTS.IDENTIFY_NONE:
      return STATES.RESULT_NONE;
    case EVENTS.IDENTIFY_ERROR:
      return STATES.ERROR;
    case EVENTS.OPEN_CONTACT:
      return STATES.CONTACT;
    case EVENTS.CONTACT_SUBMITTED:
      return STATES.CONTACT_SUCCESS;
    case EVENTS.ASK_AGAIN:
      return STATES.PANEL_INPUT;
    default:
      return state;
  }
}

/**
 * Validate a chosen file against size + type constraints.
 *
 * @param {File|{size:number,type:string}} file
 * @param {number} maxBytes
 * @param {string[]} allowedTypes
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFile(file, maxBytes, allowedTypes) {
  if (!file) {
    return { valid: false, error: 'No file selected.' };
  }
  if (
    Array.isArray(allowedTypes) &&
    allowedTypes.length > 0 &&
    !allowedTypes.includes(file.type)
  ) {
    return {
      valid: false,
      error: 'Unsupported file type. Use JPG, PNG or WEBP.',
    };
  }
  if (typeof maxBytes === 'number' && maxBytes > 0 && file.size > maxBytes) {
    return { valid: false, error: 'Image is too large.' };
  }
  return { valid: true };
}

// ---------------------------------------------------------------------------
// Backend calls
// ---------------------------------------------------------------------------

/**
 * Upload a file to S3 via a backend-issued presigned PUT URL.
 * Throws on any non-OK response.
 *
 * @returns {Promise<string>} the s3_key for the uploaded object
 */
/**
 * Wrap fetch so a network/CORS failure ("Failed to fetch", which carries no
 * HTTP status) is rethrown with the phase + URL that failed, instead of the
 * opaque default message.
 */
async function fetchOrThrow(phase, url, options) {
  try {
    return await fetch(url, options);
  } catch (e) {
    throw new Error(
      `[${phase}] network/CORS failure for ${url} — the request never reached a ` +
        `server (check the App Proxy is configured and, for S3 PUTs, the bucket CORS). ` +
        `Original: ${e.message}`
    );
  }
}

async function uploadFile(file, proxyBasePath) {
  const presignRes = await fetchOrThrow(
    'upload:presign',
    `${proxyBasePath}/api/pod_finder/upload`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_type: file.type, file_size: file.size }),
    }
  );
  if (!presignRes.ok) {
    throw new Error(`upload presign failed: HTTP ${presignRes.status}`);
  }
  const presign = await presignRes.json();

  const putRes = await fetchOrThrow('upload:s3-put', presign.upload_url, {
    method: presign.method || 'PUT',
    headers: presign.headers || { 'Content-Type': file.type },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`s3 put failed: HTTP ${putRes.status}`);
  }
  return presign.s3_key;
}

/**
 * Identify the machine. Throws on any non-OK response.
 *
 * @returns {Promise<object>} the identify response { category, match, confidence, title, message }
 */
async function identify(payload, proxyBasePath) {
  const res = await fetchOrThrow(
    'identify',
    `${proxyBasePath}/api/pod_finder/identify`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    throw new Error(`identify failed: HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Submit the no-match contact form. The backend opens a Gorgias ticket.
 * Throws on any non-OK response.
 *
 * @returns {Promise<object>} { status, ticket_id }
 */
async function submitContactTicket(payload, proxyBasePath) {
  const res = await fetchOrThrow(
    'contact',
    `${proxyBasePath}/api/pod_finder/contact`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    throw new Error(`contact failed: HTTP ${res.status}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// DOM controller
// ---------------------------------------------------------------------------

class GuidedJourney {
  constructor(root) {
    this.root = root;
    // Standalone mode (sections/find-my-pod.liquid, header group): panel-only —
    // starts closed and never touches the shared localStorage key, which belongs
    // to the build-bundle takeover (restoring it here would auto-open the panel
    // on every page; persisting would corrupt the takeover's journey state).
    this.standalone = root.hasAttribute('data-gj-standalone');
    this.proxyBasePath = root.dataset.proxyBasePath || '/apps/mr-middleware';
    this.maxBytes = (parseInt(root.dataset.maxImageMb, 10) || 10) * 1024 * 1024;
    this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    this.blocks = this.readBlocks();
    this.mode = MODES.SUBSCRIBE;
    this.selectedFile = null;
    this.lastResult = null;
    this.lastS3Key = null;
    this.lastText = '';
    this.uploading = false;
    this._objectUrl = null;

    // UBB-224 GA4: journey_step per step entry (consecutive-dedupe, so
    // hero→selector stays one product_type view but panel→selector→panel
    // re-fires). Standalone (header Find My Pod) is outside the funnel.
    this.stepTracker = createStepTracker(pushToDataLayer);

    this.cacheEls();
    this.bindEvents();
    this.restore();
  }

  trackJourneyStep() {
    if (this.standalone) return;
    const step = mapStateToStep(this.state);
    if (!step) return;
    if (this.stepTracker.track(step)) {
      recordJourneyStep(getSessionStorage(), step);
    }
  }

  readBlocks() {
    return Array.from(this.root.querySelectorAll('[data-gj-card]')).map(
      (el) => ({
        categoryKey: el.dataset.categoryKey,
        bundleUrl: el.dataset.bundleUrl,
        bundleUrlOnetime: el.dataset.bundleUrlOnetime || '',
        heading: el.dataset.heading || '',
        description: el.dataset.description || '',
        image: el.dataset.image || '',
      })
    );
  }

  cacheEls() {
    const $ = (sel) => this.root.querySelector(sel);
    this.views = {
      [STATES.HERO]: $('[data-gj-view="hero"]'),
      [STATES.SELECTOR]: $('[data-gj-view="selector"]'),
    };
    this.panel = $('[data-gj-panel]');
    this.panelStages = {
      input: $('[data-gj-stage="input"]'),
      loading: $('[data-gj-stage="loading"]'),
      result: $('[data-gj-stage="result"]'),
    };
    this.inputFooter = $('[data-gj-stage-footer="input"]');
    this.uploadEls = {
      default: $('[data-gj-upload="default"]'),
      uploading: $('[data-gj-upload="uploading"]'),
      uploaded: $('[data-gj-upload="uploaded"]'),
      error: $('[data-gj-upload="error"]'),
    };
    this.uploadThumb = $('[data-gj-upload-thumb]');
    this.uploadedFilename = $('[data-gj-uploaded-filename]');
    this.fileInput = $('[data-gj-file]');
    this.textInput = $('[data-gj-text]');
    this.submitBtn = $('[data-gj-submit]');
    this.resultMount = $('[data-gj-result-mount]');
  }

  bindEvents() {
    this.root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-gj-event]');
      if (trigger && this.root.contains(trigger)) {
        this.handleEvent(trigger.dataset.gjEvent, trigger);
        return;
      }
      const card = e.target.closest('[data-gj-card]');
      if (card && this.root.contains(card)) {
        this.selectOption(card);
      }
    });

    if (this.fileInput) {
      this.fileInput.addEventListener('change', () => this.onFileChosen());
    }

    // Contact form (no-match → Gorgias ticket) is rendered into the result
    // mount; catch its submit here (supports Enter + the Submit button).
    this.root.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-gj-contact-form]');
      if (form && this.root.contains(form)) {
        e.preventDefault();
        this.submitContact(form);
      }
    });

    this.bindDragAndDrop();
  }

  /**
   * Drag & drop support on the "Snap your machine" dropzone. Dropped images
   * go through the same validation/preview path as the file picker.
   */
  bindDragAndDrop() {
    const dz = this.uploadEls.default;
    if (!dz) return;

    const stop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    ['dragenter', 'dragover'].forEach((ev) =>
      dz.addEventListener(ev, (e) => {
        stop(e);
        dz.classList.add('is-dragover');
      })
    );

    ['dragleave', 'dragend'].forEach((ev) =>
      dz.addEventListener(ev, (e) => {
        stop(e);
        dz.classList.remove('is-dragover');
      })
    );

    dz.addEventListener('drop', (e) => {
      stop(e);
      dz.classList.remove('is-dragover');
      const file =
        e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.setFile(file);
    });
  }

  handleEvent(event, trigger) {
    switch (event) {
      case EVENTS.GET_STARTED:
      case EVENTS.BACK:
        this.transition(event);
        break;
      case EVENTS.ASK_BREWSTER:
        this.transition(event);
        break;
      case EVENTS.CLOSE_PANEL:
        this.transition(event);
        break;
      case EVENTS.SUBMIT:
        this.submit();
        break;
      case EVENTS.ASK_AGAIN:
        this.resetPanelInput();
        this.transition(event);
        break;
      case 'browse':
        if (this.fileInput) this.fileInput.click();
        break;
      case 'retry':
        this.removeUpload();
        break;
      case 'remove-upload':
        this.removeUpload();
        break;
      case EVENTS.OPEN_CONTACT:
        this.transition(event);
        break;
      case 'select-result':
        this.redirect(trigger.dataset.bundleUrl);
        break;
      // UBB-418 — purchase type chosen on the hero, then straight to the selector.
      case EVENTS.CHOOSE_SUBSCRIBE:
        this.setMode(MODES.SUBSCRIBE);
        this.transition(EVENTS.GET_STARTED);
        break;
      case EVENTS.CHOOSE_ONETIME:
        this.setMode(MODES.ONETIME);
        this.transition(EVENTS.GET_STARTED);
        break;
      case EVENTS.TOGGLE_MODE:
        this.setMode(otherMode(this.mode));
        break;
      default:
        break;
    }
  }

  transition(event) {
    this.state = nextState(this.state, event);
    this.render();
    this.persist();
  }

  /**
   * Cards are single controls again: the purchase type was chosen on the hero, so a
   * click just opens the bundle URL for the active mode. Cards without a URL for that
   * mode are hidden by applyMode(), so this should always resolve.
   */
  selectOption(card) {
    if (!card) return;
    const block = this.blocks.find(
      (b) => b.categoryKey === card.dataset.categoryKey
    );
    this.redirect(bundleUrlForMode(block, this.mode));
  }

  setMode(mode) {
    this.mode = mode;
    this.applyMode();
  }

  /**
   * Sync everything that depends on the active purchase mode: the pill label, whether
   * the Change toggle is offered at all, and which option cards are shown — a card with
   * no bundle URL for this mode is hidden, so a customer is never offered a category
   * they cannot buy the way they chose.
   */
  applyMode() {
    const pill = this.root.querySelector('[data-gj-mode]');
    const label = this.root.querySelector('[data-gj-mode-label]');
    if (pill && label) {
      label.textContent =
        this.mode === MODES.ONETIME
          ? pill.dataset.labelOnetime || ''
          : pill.dataset.labelSubscribe || '';
    }
    // Nothing to switch to if no card can be bought the other way — hide the toggle
    // rather than let it strand the customer on an empty grid.
    const change = this.root.querySelector('[data-gj-mode-change]');
    if (change) {
      change.hidden = !hasCardsForMode(this.blocks, otherMode(this.mode));
    }
    this.root.querySelectorAll('[data-gj-card]').forEach((card) => {
      const block = this.blocks.find(
        (b) => b.categoryKey === card.dataset.categoryKey
      );
      card.hidden = !bundleUrlForMode(block, this.mode);
    });
  }

  redirect(url) {
    if (!url) return;
    this.state = STATES.SELECTOR;
    this.persist();
    window.location.assign(url);
  }

  onFileChosen() {
    const file = this.fileInput.files && this.fileInput.files[0];
    if (file) this.setFile(file);
  }

  /**
   * Show one of the upload sub-states: default | uploading | uploaded | error.
   */
  setUploadState(name, message) {
    Object.entries(this.uploadEls).forEach(([key, el]) => {
      if (el) el.hidden = key !== name;
    });
    if (name === 'error') {
      const el = this.uploadEls.error;
      const msg = el && el.querySelector('[data-gj-upload-error-msg]');
      if (msg && message) msg.textContent = message;
    }
  }

  /**
   * Accept a file from the picker or drag & drop, then upload it immediately.
   * Invalid files go straight to the error state.
   */
  setFile(file) {
    const check = validateFile(file, this.maxBytes, this.allowedTypes);
    if (!check.valid) {
      this.clearUpload();
      this.setUploadState('error', check.error);
      return;
    }
    this.uploadSelectedFile(file);
  }

  /**
   * Upload on select/drop: show a preview + "uploading", then the success
   * state (with remove) or the error state (with try again).
   */
  async uploadSelectedFile(file) {
    this._setObjectUrl(file);
    this.selectedFile = file;
    this.lastS3Key = null;
    this.uploading = true;
    if (this.uploadedFilename) this.uploadedFilename.textContent = file.name;
    this.setUploadState('uploading');

    try {
      const s3Key = await uploadFile(file, this.proxyBasePath);
      this.lastS3Key = s3Key;
      this.uploading = false;
      this.setUploadState('uploaded');
    } catch (err) {
      console.error('[guided-journey] upload error', err);
      this.uploading = false;
      this.selectedFile = null;
      this.lastS3Key = null;
      this.setUploadState('error', 'Oops, let’s try that again');
    }
  }

  _setObjectUrl(file) {
    this._revokeObjectUrl();
    this._objectUrl = URL.createObjectURL(file);
    if (this.uploadThumb) this.uploadThumb.src = this._objectUrl;
  }

  _revokeObjectUrl() {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = null;
    }
  }

  /** Forget the current file/upload and reset the picker (keeps current view). */
  clearUpload() {
    this._revokeObjectUrl();
    this.selectedFile = null;
    this.lastS3Key = null;
    this.uploading = false;
    if (this.fileInput) this.fileInput.value = '';
    if (this.uploadThumb) this.uploadThumb.removeAttribute('src');
    if (this.uploadedFilename) this.uploadedFilename.textContent = '';
  }

  /** Remove the uploaded image and return to the dropzone. */
  removeUpload() {
    this.clearUpload();
    this.setUploadState('default');
  }

  resetPanelInput() {
    this.removeUpload();
    if (this.textInput) this.textInput.value = '';
    this.lastText = '';
    this.lastResult = null;
  }

  async submit() {
    // The image (if any) is already uploaded on select/drop — submit just
    // identifies using the stored s3_key. Don't submit mid-upload.
    if (this.uploading) return;

    const text = this.textInput ? this.textInput.value.trim() : '';
    const hasImage = Boolean(this.lastS3Key);
    if (!hasImage && !text) {
      return;
    }

    this.state = nextState(STATES.PANEL_INPUT, EVENTS.SUBMIT);
    this.render();
    this.persist();

    try {
      const payload = {};
      if (hasImage) payload.s3_key = this.lastS3Key;
      if (text) {
        payload.text = text;
        this.lastText = text;
      }
      const result = await identify(payload, this.proxyBasePath);
      this.lastResult = result;
      const matched = result.match && result.category !== 'none';
      this.transition(matched ? EVENTS.IDENTIFY_MATCH : EVENTS.IDENTIFY_NONE);
    } catch (err) {
      console.error('[guided-journey] identify error', err);
      this.transition(EVENTS.IDENTIFY_ERROR);
    }
  }

  /**
   * Submit the no-match contact form -> backend opens a Gorgias ticket.
   * Carries the Find My Pod context (typed description, uploaded photo key,
   * partial AI result) so support can follow up.
   */
  async submitContact(form) {
    const get = (name) => {
      const el = form.querySelector(`[name="${name}"]`);
      return el ? el.value.trim() : '';
    };
    const errEl = form.querySelector('[data-gj-contact-error]');
    const submitBtn = form.querySelector('[data-gj-contact-submit]');

    const payload = {
      full_name: get('full_name'),
      email: get('email'),
      phone: get('phone'),
      contact_preference: get('contact_preference') || 'email',
    };
    if (!payload.full_name || !payload.email) {
      if (errEl) errEl.textContent = 'Please enter your name and email.';
      return;
    }
    // Find My Pod context.
    if (this.lastText) payload.text = this.lastText;
    if (this.lastS3Key) payload.s3_key = this.lastS3Key;
    // Echo the identify result back so the ticket says what the assistant
    // decided and why, rather than making support guess from category alone
    // (UBB-339). Without these the backend falls back to deriving `outcome`
    // from category + confidence.
    const r = this.lastResult || {};
    if (r.category) payload.category = r.category;
    if (typeof r.confidence === 'number') payload.confidence = r.confidence;
    if (r.outcome) payload.outcome = r.outcome;
    if (typeof r.identified === 'boolean') payload.identified = r.identified;
    if (r.machine) payload.machine = r.machine;
    if (r.reasoning) payload.reasoning = r.reasoning;

    if (errEl) errEl.textContent = '';
    if (submitBtn) submitBtn.disabled = true;
    try {
      await submitContactTicket(payload, this.proxyBasePath);
      this.transition(EVENTS.CONTACT_SUBMITTED);
    } catch (err) {
      console.error('[guided-journey] contact error', err);
      if (submitBtn) submitBtn.disabled = false;
      if (errEl) {
        errEl.textContent =
          "Sorry, we couldn't send that just now. Please try again.";
      }
    }
  }

  // -- rendering -------------------------------------------------------------

  render() {
    this.trackJourneyStep();
    this.applyMode();

    const panelOpen = [
      STATES.PANEL_INPUT,
      STATES.PANEL_LOADING,
      STATES.RESULT_MATCH,
      STATES.RESULT_NONE,
      STATES.CONTACT,
      STATES.CONTACT_SUCCESS,
      STATES.ERROR,
    ].includes(this.state);

    // Top-level views: hero or selector. The panel sits over the selector.
    const baseView = this.state === STATES.HERO ? STATES.HERO : STATES.SELECTOR;
    if (this.views[STATES.HERO])
      this.views[STATES.HERO].hidden = baseView !== STATES.HERO;
    if (this.views[STATES.SELECTOR]) {
      this.views[STATES.SELECTOR].hidden = baseView !== STATES.SELECTOR;
    }

    if (this.panel) this.panel.hidden = !panelOpen;
    if (!panelOpen) return;

    const showStage = (name) => {
      Object.entries(this.panelStages).forEach(([key, el]) => {
        if (el) el.hidden = key !== name;
      });
    };

    if (this.inputFooter)
      this.inputFooter.hidden = this.state !== STATES.PANEL_INPUT;

    if (this.state === STATES.PANEL_INPUT) {
      showStage('input');
    } else if (this.state === STATES.PANEL_LOADING) {
      showStage('loading');
    } else {
      showStage('result');
      this.renderResult();
    }
  }

  renderResult() {
    if (!this.resultMount) return;
    if (this.state === STATES.ERROR) {
      this.resultMount.innerHTML = this.errorMarkup();
      return;
    }
    if (this.state === STATES.CONTACT) {
      this.resultMount.innerHTML = this.contactMarkup();
      const nameInput = this.resultMount.querySelector('[name="full_name"]');
      if (nameInput) nameInput.focus();
      return;
    }
    if (this.state === STATES.CONTACT_SUCCESS) {
      this.resultMount.innerHTML = this.contactSuccessMarkup();
      return;
    }
    const result = this.lastResult || {};
    const matched = this.state === STATES.RESULT_MATCH;
    const option = mapCategoryToOption(result.category, matched, this.blocks);
    this.resultMount.innerHTML = matched
      ? this.matchMarkup(result, option)
      : this.noMatchMarkup(result, option);
  }

  /**
   * Buttons for a result's bundle CTAs, styled to match the hero: the subscription
   * CTA is the brand's green "Bundle & save" button and one-time is the dark primary,
   * keyed off the action's purchase type rather than its position.
   */
  resultActionsMarkup(option) {
    const cfg = this.root.dataset;
    return resultActions(option, {
      standalone: this.standalone,
      mode: this.mode,
      labels: {
        subscribe: cfg.resultSubscribeLabel,
        onetime: cfg.resultOnetimeLabel,
      },
    })
      .map(
        (action) =>
          `<button type="button" class="button button--${
            action.type === MODES.ONETIME ? 'primary' : 'bundle'
          } button--no-arrow gj-result__action" data-gj-event="select-result" data-bundle-url="${escapeAttr(
            action.url
          )}">${escapeHtml(action.label)}</button>`
      )
      .join('');
  }

  matchMarkup(result, option) {
    const heading =
      result.title ||
      (option ? `Works with our ${option.heading}!` : 'Match found!');
    const message = result.message || (option ? option.description : '');
    const thumb =
      option && option.image
        ? `<img class="gj-result__thumb" src="${option.image}" alt="" />`
        : '';
    const actions = this.resultActionsMarkup(option);
    return `
      <div class="gj-result">
        <div class="gj-result__head">
          <div class="gj-result__media">
            ${thumb}
            <h3 class="guided-journey__title gj-result__heading">${escapeHtml(heading)}</h3>
          </div>
          <span class="gj-result__badge gj-result__badge--match">Match &#10003;</span>
        </div>
        <p class="gj-result__message">${escapeHtml(message)}</p>
        <div class="gj-result__actions">${actions}</div>
      </div>`;
  }

  noMatchMarkup(result, beansOption) {
    const cfg = this.root.dataset;
    const heading = cfg.nomatchHeading || 'HMMM, NO POD MATCH';
    const message = noMatchMessage(cfg.nomatchMessage, result.message);
    const actions = this.resultActionsMarkup(beansOption);
    const helpLabel = cfg.helpButtonLabel || "Can't find your machine?";
    return `
      <div class="gj-result">
        <div class="gj-result__head">
          <h3 class="guided-journey__title gj-result__heading">${escapeHtml(heading)}</h3>
          <span class="gj-result__badge gj-result__badge--none">Not a match &#10005;</span>
        </div>
        <p class="gj-result__message">${escapeHtml(message)}</p>
        <div class="gj-result__actions">
          ${actions}
          <button type="button" class="guided-journey__button guided-journey__button--outline"
            data-gj-event="open-contact">${escapeHtml(helpLabel)}</button>
        </div>
      </div>`;
  }

  contactMarkup() {
    const cfg = this.root.dataset;
    const intro =
      cfg.contactIntro ||
      'No match found! Pop in your details and our team will track down the right pods for you.';
    const heading = cfg.contactHeading || 'Your Contact Details';
    const submitLabel = cfg.contactSubmitLabel || 'Submit';
    const helper =
      cfg.contactHelperText || "We'll get back to you within 24 hours";
    return `
      <div class="gj-contact">
        <p class="gj-result__message gj-contact__intro">${escapeHtml(intro)}</p>
        <form class="gj-contact__form" data-gj-contact-form novalidate>
          <h3 class="gj-contact__heading">${escapeHtml(heading)}</h3>
          <input class="gj-contact__field" type="text" name="full_name"
            placeholder="Full Name" autocomplete="name" required />
          <input class="gj-contact__field" type="email" name="email"
            placeholder="Email address" autocomplete="email" required />
          <input class="gj-contact__field" type="tel" name="phone"
            placeholder="Phone number" autocomplete="tel" />
          <select class="gj-contact__field gj-contact__select" name="contact_preference"
            aria-label="Contact preference">
            <option value="" disabled selected>Contact preference</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
          <p class="gj-contact__error" data-gj-contact-error role="alert"></p>
          <button type="submit" class="guided-journey__button guided-journey__button--submit"
            data-gj-contact-submit>${escapeHtml(submitLabel)}</button>
          <p class="gj-contact__helper">${escapeHtml(helper)}</p>
        </form>
      </div>`;
  }

  contactSuccessMarkup() {
    const cfg = this.root.dataset;
    const message =
      cfg.contactSuccessMessage ||
      'Thanks! Our team will track down the right pods and get back to you within 24 hours.';
    return `
      <div class="gj-result">
        <div class="gj-result__head">
          <h3 class="guided-journey__title gj-result__heading">All done!</h3>
          <span class="gj-result__badge gj-result__badge--match">Sent &#10003;</span>
        </div>
        <p class="gj-result__message">${escapeHtml(message)}</p>
      </div>`;
  }

  errorMarkup() {
    return `
      <div class="gj-result">
        <div class="gj-result__head">
          <h3 class="guided-journey__title gj-result__heading">Something went wrong</h3>
          <span class="gj-result__badge gj-result__badge--none">Error</span>
        </div>
        <p class="gj-result__message">We couldn't reach Brewster just now. Please try again.</p>
        <div class="gj-result__actions">
          <button type="button" class="guided-journey__button guided-journey__button--dark"
            data-gj-event="ask-again">Try again</button>
        </div>
      </div>`;
  }

  // -- persistence -----------------------------------------------------------

  /** Open the Find My Pod panel from outside the root (header Ask Brewster CTA). */
  openPanel() {
    this.transition(EVENTS.ASK_BREWSTER);
  }

  persist() {
    if (this.standalone || !PERSIST_JOURNEY) return;
    try {
      const data = {
        state: this.state,
        mode: this.mode,
        savedAt: Date.now(),
        lastResult: this.lastResult,
        text: this.textInput ? this.textInput.value : '',
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage unavailable (private mode) — non-fatal.
    }
  }

  restore() {
    if (this.standalone) {
      // Panel closed until a [data-find-my-pod-open] trigger opens it. HERO renders
      // nothing here (no hero/selector views in the standalone root — the guards in
      // render() skip them) and keeps the panel hidden.
      this.state = STATES.HERO;
      this.render();
      return;
    }
    let data = null;
    if (PERSIST_JOURNEY) {
      try {
        data = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      } catch {
        data = null;
      }
    }
    if (data && data.state && isStateFresh(data)) {
      this.state = data.state;
      this.mode = data.mode === MODES.ONETIME ? MODES.ONETIME : MODES.SUBSCRIBE;
      this.lastResult = data.lastResult || null;
      if (this.textInput && typeof data.text === 'string') {
        this.textInput.value = data.text;
      }
    } else {
      this.state = STATES.HERO;
    }
    this.render();
  }
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

// ---------------------------------------------------------------------------
// Header trigger: [data-find-my-pod-open] (the Ask Brewster CTA in the desktop
// mega menu + mobile drawer — snippets/ask-brewster.liquid) opens the standalone
// panel rendered by sections/find-my-pod.liquid. The trigger lives OUTSIDE the
// panel's [data-gj-root], so it can't ride the controller's own delegation.
// ---------------------------------------------------------------------------

/** Close the desktop mega menu — mirrors megamenu-nav-component's setItemState. */
function closeMegaMenus() {
  document
    .querySelectorAll('.list-menu--megamenu.is-megamenu-open')
    .forEach((item) => {
      item.classList.remove('is-megamenu-open');
      const trigger = item.querySelector('.header__menu-item');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
}

/**
 * Close the mobile burger drawer via the vendored close path — same shape as
 * src/js/objects/global/header-drawer-close.js: the overlay click routes to
 * HeaderDrawer.closeMenuDrawer (unlocks scroll + resets aria/focus).
 */
function closeHeaderDrawer() {
  if (!document.querySelector('header-drawer details[open]')) return;
  const overlay = document.querySelector('.header-drawer-overlay');
  if (overlay) {
    overlay.click();
    return;
  }
  const summary = document.querySelector('header-drawer summary');
  if (summary) summary.click();
}

function bindFindMyPodTriggers() {
  // Delegated so it survives header section re-renders in the theme editor.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-find-my-pod-open]');
    if (!trigger) return;
    const root = document.querySelector('[data-gj-standalone]');
    const controller = root && root.gjController;
    if (!controller) return;
    e.preventDefault();
    closeMegaMenus();
    closeHeaderDrawer();
    controller.openPanel();
  });
}

/**
 * UB-QA-65 (Figma QA, 2026-07-20): the chrome-less `build-bundle` takeover's page-level
 * close (X) used to always navigate to `routes.root_url` (sections/guided-journey.liquid's
 * `href` — kept as the no-JS/no-history fallback). Ada: "when I close the modal, I get
 * redirected back to home. I should stay on the same spot from where I clicked the CTA
 * originally." So prefer `history.back()` whenever we got here via an in-app click (there IS
 * browser history + the referrer is same-origin); only fall through to the anchor's href
 * (home) when the page was opened directly (new tab / no history / cross-origin referrer),
 * where "back" would either do nothing or leave the site.
 */
function bindPageCloseButton() {
  document.querySelectorAll('.guided-journey__close').forEach((closeBtn) => {
    if (closeBtn.dataset.gjCloseInit) return;
    closeBtn.dataset.gjCloseInit = '1';
    closeBtn.addEventListener('click', (e) => {
      // Closing the takeover ends the journey — this is the ONLY place the
      // persisted state is cleared (card/result redirects keep it).
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // localStorage unavailable (private mode) — non-fatal.
      }
      const sameOriginReferrer =
        document.referrer &&
        (() => {
          try {
            return new URL(document.referrer).origin === window.location.origin;
          } catch {
            return false;
          }
        })();
      if (window.history.length > 1 && sameOriginReferrer) {
        e.preventDefault();
        window.history.back();
      }
    });
  });
}

function init() {
  document.querySelectorAll('[data-gj-root]').forEach((root) => {
    if (!root.dataset.gjInit) {
      root.dataset.gjInit = '1';
      root.gjController = new GuidedJourney(root);
    }
  });
  if (!document.body.dataset.fmpTriggersInit) {
    document.body.dataset.fmpTriggersInit = '1';
    bindFindMyPodTriggers();
  }
  bindPageCloseButton();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
